/**
 * @typedef {import('../types').FiltersStore} FiltersStore
 * @typedef {{ label: string, key: string, route?: string, category?: string|null, count?: number }} QuickMarketEntry
 */

const QUICK_CHIP_ATTR = 'data-props-quick-chip';

/**
 * Quick chips renderer: replaces legacy inline behaviour with config-driven markup.
 * @param {FiltersStore} store
 */
export function registerQuickChipsModule(store) {
  if (!store || typeof document === 'undefined') {
    return;
  }

  const root = document.querySelector('[data-props-quick-chips]');
  if (!root) {
    return;
  }

  const scopeNode = root.closest('.props-odds') || null;

  /** @type {QuickMarketEntry[]} */
  const quickMarkets = Array.isArray(store?.config?.markets?.quick)
    ? store.config.markets.quick
    : [];

  if (!quickMarkets.length) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[filters] quick chips config missing quick markets');
    }
    return;
  }

  const abbreviations =
    (store.config?.markets?.abbreviations &&
      typeof store.config.markets.abbreviations === 'object')
      ? store.config.markets.abbreviations
      : {};

  const routeMap =
    (store.config?.markets?.routeMap &&
      typeof store.config.markets.routeMap === 'object')
      ? store.config.markets.routeMap
      : {};

  const countsFromConfig =
    (store.config?.markets?.counts &&
      typeof store.config.markets.counts === 'object')
      ? store.config.markets.counts
      : {};

  const inlineEnabled = !!store.config?.featureFlags?.inlineMarketsEnabled;
  const chipsByKey = new Map();
  const quickKeys = new Set();
  const quickLabels = new Map(); // key -> label

  /**
   * Remove legacy quick chip buttons rendered by PHP to avoid duplicate controls.
   */
  function cleanupLegacyButtons() {
    root.querySelectorAll('button.chip[data-market]').forEach((btn) => {
      if (btn.hasAttribute('data-props-more-trigger')) {
        return;
      }
      if (!btn.hasAttribute(QUICK_CHIP_ATTR)) {
        btn.remove();
      }
    });
  }

  /**
   * Compute display label (abbreviation fallback).
   * @param {QuickMarketEntry} market
   */
  function getDisplayLabel(market) {
    const label = market.label || '';
    if (
      label &&
      Object.prototype.hasOwnProperty.call(abbreviations, label) &&
      abbreviations[label]
    ) {
      return abbreviations[label];
    }
    return label;
  }

  /**
   * Format the numeric badge value.
   * @param {number} value
   */
  function formatCount(value) {
    if (!Number.isFinite(value) || value <= 0) {
      return '';
    }
    return value > 999 ? '999+' : String(value);
  }

  /**
   * Update a chip's badge with the specified count.
   * @param {HTMLButtonElement} button
   * @param {number} count
   */
  function updateChipCount(button, count) {
    const badge =
      button.querySelector('.chip-count') || document.createElement('span');
    badge.className = 'chip-count';
    const formatted = formatCount(count);
    if (formatted) {
      badge.textContent = formatted;
      badge.hidden = false;
    } else {
      badge.textContent = '';
      badge.hidden = true;
    }
    if (!badge.parentElement) {
      button.appendChild(badge);
    }
  }

  /**
   * Update quick chips badge counts from the current dataset payload.
   */
  function refreshCountsFromDataset() {
    const raw = root.getAttribute('data-market-counts') || '';
    if (!raw) {
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[filters] failed to parse market counts payload', error);
      }
      return;
    }
    if (!parsed || typeof parsed !== 'object') {
      return;
    }
    chipsByKey.forEach(({ button, market }) => {
      const key = market.key || '';
      const label = market.label || '';
      const nextCount =
        (key && parsed[key] !== undefined
          ? parsed[key]
          : parsed[label] !== undefined
            ? parsed[label]
            : undefined);
      if (nextCount !== undefined) {
        updateChipCount(button, Number(nextCount) || 0);
      }
    });
  }

  /**
   * Resolve a navigation target URL for the given market entry.
   * @param {QuickMarketEntry} market
   */
  function resolveRoute(market) {
    if (market.route) {
      return market.route;
    }
    if (market.key && routeMap[market.key]) {
      return routeMap[market.key];
    }
    return '';
  }

  /**
   * Clear active filters (matchups, positions, search) when switching markets.
   */
  function clearAllFilters() {
    const { actions } = store || {};
    // Reset inline store state so newly loaded markets are unfiltered.
    if (actions?.setActiveMatchup) {
      actions.setActiveMatchup('');
    } else {
      store.setState?.({ activeMatchupId: '' });
    }
    if (actions?.setPositionFilters) {
      actions.setPositionFilters([]);
    } else {
      store.setState?.({ positionFilters: new Set() });
    }
    if (actions?.setSearchTerm) {
      actions.setSearchTerm('');
    } else {
      store.setState?.({ searchTerm: '' });
    }

    // Clear any search input directly
    const searchInput = document.querySelector('[data-props-player-search]');
    if (searchInput instanceof HTMLInputElement) {
      searchInput.value = '';
    }

    // Clear legacy/inline positions helper if present.
    if (window.propsPositionsState?.clear) {
      try {
        window.propsPositionsState.clear({ silent: true });
      } catch {
        // ignore legacy clear failures
      }
    }

    // Trigger a filter update event to refresh the table
    const event = new CustomEvent('props:filters:cleared', {
      bubbles: true,
      detail: { source: 'market-change' },
    });
    document.dispatchEvent(event);
  }

  /**
   * Navigate to the selected market (inline or hard navigation).
   * @param {QuickMarketEntry} market
   */
  function navigateToMarket(market, options = {}) {
    const currentState = store.getState();
    const isMarketChange =
      typeof options.isMarketChange === 'boolean'
        ? options.isMarketChange
        : currentState.activeMarketKey !== market.key;

    // Clear filters when switching markets
    if (isMarketChange) {
      clearAllFilters();
    }

    const scope = root.closest('.props-market-filter') || root;
    const currentRoute = resolveRoute(market);
    const detail = {
      marketKey: market.key,
      marketLabel: market.label,
    };
    if (currentRoute) {
      detail.route = currentRoute;
    }

    const fallback = () => {
      if (currentRoute) {
        window.location.assign(currentRoute);
        return;
      }
      try {
        const url = new URL(window.location.href);
        // When changing markets, clear ALL other params
        if (isMarketChange) {
          // Clear all search params
          const keysToDelete = [...url.searchParams.keys()];
          keysToDelete.forEach(key => url.searchParams.delete(key));
          // Add only the market param
          if (market.key) {
            url.searchParams.set('market', market.key);
          }
        } else {
          // Not changing markets, just update the market param
          if (market.key) {
            url.searchParams.set('market', market.key);
          } else {
            url.searchParams.delete('market');
          }
        }
        // Update the URL without reloading the page
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        if (market.key) {
          window.location.search = `?market=${encodeURIComponent(market.key)}`;
        }
      }
    };

    if (inlineEnabled && typeof window.propsInlineNavigate === 'function') {
      // Pass a modified detail object that tells inline nav to clear filters
      const enhancedDetail = {
        ...detail,
        clearFilters: isMarketChange
      };
      window.propsInlineNavigate(scope, 'market', enhancedDetail, fallback);
      return;
    }
    fallback();
  }

  /**
   * Handle chip click interaction.
   * @param {MouseEvent} event
   * @param {QuickMarketEntry} market
   */
  function handleChipClick(event, market) {
    event.preventDefault();

    const currentState = store.getState();
    const isMarketChange = currentState.activeMarketKey !== market.key;
    if (!isMarketChange) {
      // Still close the more dialog if open.
      store.actions.setDialogOpen?.('more', false);
      return;
    }

    // Update store state
    if (store.actions && typeof store.actions.setActiveMarket === 'function') {
      store.actions.setActiveMarket(market.key);
    } else {
      store.setState?.({ activeMarketKey: market.key });
    }
    if (store.actions && typeof store.actions.setDialogOpen === 'function') {
      store.actions.setDialogOpen('more', false);
    }

    // Navigate to the market (will handle filter clearing internally)
    navigateToMarket(market, { isMarketChange });
  }

  /**
   * Render quick chips based on config payload.
   */
  function renderChips() {
    cleanupLegacyButtons();
    const fragment = document.createDocumentFragment();

    quickMarkets.forEach((market) => {
      const key = market.key || '';
      if (!key) {
        return;
      }
      const displayLabel = getDisplayLabel(market);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'chip';
      button.setAttribute(QUICK_CHIP_ATTR, 'true');
      button.setAttribute('data-market', market.label);
      button.setAttribute('data-market-key', key);
      const route = resolveRoute(market);
      if (route) {
        button.setAttribute('data-market-route', route);
      }

      const labelSpan = document.createElement('span');
      labelSpan.className = 'chip-label';
      labelSpan.textContent = displayLabel || market.label;
      button.appendChild(labelSpan);

      const initialCount =
        typeof market.count === 'number'
          ? market.count
          : countsFromConfig[key] ?? countsFromConfig[market.label] ?? 0;
      updateChipCount(button, Number(initialCount) || 0);

      button.addEventListener('click', (event) => handleChipClick(event, market));

      fragment.appendChild(button);
      chipsByKey.set(key, { button, market });
      quickKeys.add(key);
      quickLabels.set(key, market.label || '');
    });

    root.appendChild(fragment);
  }

  /**
   * Synchronise chip active state with the store.
   * @param {import('../types').FiltersState} state
   */
  function updateActiveState(state) {
    const { activeMarketKey } = state;
    const activeKey = activeMarketKey || '';
    chipsByKey.forEach(({ button, market }) => {
      const isActive = activeKey === (market.key || '');
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (isActive) {
        root.setAttribute('data-current-market', market.label || '');
        root.setAttribute('data-current-market-key', market.key || '');
      }
    });

    const moreTrigger = root.querySelector('[data-props-more-trigger="true"]');
    if (moreTrigger instanceof HTMLElement) {
      const hasQuickMatch = quickKeys.has(activeKey);
      moreTrigger.setAttribute('aria-pressed', hasQuickMatch ? 'false' : 'true');
      moreTrigger.classList.toggle('active', !hasQuickMatch);
    }
  }

  renderChips();
  refreshCountsFromDataset();
  updateActiveState(store.getState());

  const unsubscribe = store.subscribe(updateActiveState);

  /**
   * When inline refreshes complete, sync the latest market counts payload from
   * the scoped props-odds container onto the quick chips root, then refresh
   * chip badges. This replaces the legacy inline PHP/JS observer that used to
   * keep counts in sync.
   */
  function syncCountsFromScope() {
    if (!scopeNode || !(scopeNode instanceof HTMLElement)) {
      return;
    }
    try {
      const latestCounts = scopeNode.getAttribute('data-market-counts') || '';
      if (latestCounts) {
        root.setAttribute('data-market-counts', latestCounts);
      } else {
        root.removeAttribute('data-market-counts');
      }
      refreshCountsFromDataset();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[filters] failed to sync market counts from scope', error);
      }
    }
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'data-market-counts'
      ) {
        refreshCountsFromDataset();
      }
    }
  });

  observer.observe(root, {
    attributes: true,
    attributeFilter: ['data-market-counts'],
  });

  if (scopeNode && !scopeNode.__filtersCountsBound) {
    scopeNode.addEventListener('propsOdds:refresh:success', syncCountsFromScope, true);
    scopeNode.__filtersCountsBound = true;
  }

  root.addEventListener(
    'filters:teardown',
    () => {
      observer.disconnect();
      unsubscribe();
      chipsByKey.clear();
      if (scopeNode && scopeNode.__filtersCountsBound) {
        try {
          scopeNode.removeEventListener(
            'propsOdds:refresh:success',
            syncCountsFromScope,
            true,
          );
        } catch {
          // ignore listener removal errors
        }
        scopeNode.__filtersCountsBound = false;
      }
    },
    { once: true }
  );

  if (process.env.NODE_ENV !== 'production') {
    console.debug('[filters] quick chips initialised', {
      quickMarkets,
      inlineEnabled,
    });
  }
}
